import PropTypes from 'prop-types';
import CorgixLink from '@veeva/corgix/link';
import { css } from '@emotion/react';

const propTypes = {
    /**
     * Label of the <code>Link</code>. An Icon can be passed in as a child as well. <br />
     * Examples: <code><Link><Icon type="user"/>User</Link></code>
     */
    children: PropTypes.node,

    /**
     * CSS class name applied to component.
     */
    className: PropTypes.string,

    /**
     * Emotion css prop.
     */
    css: PropTypes.oneOfType([PropTypes.object, PropTypes.func, PropTypes.array]),

    /**
     * If <code>true</code>, link is in a disabled state.
     */
    disabled: PropTypes.bool,

    /**
     * Contains a URL or a URL fragment.
     */
    href: PropTypes.string,

    /**
     * Reference to the <a> DOM node. Accepts callback refs or refs created by the
     * <code>useRef</code> hook or <code>createRef</code> method from React.
     */
    nodeRef: PropTypes.oneOfType([
        PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
        PropTypes.func,
    ]),

    /**
     * Callback fired when link is clicked or when <code>SPACE</code> or <code>RETURN</code> keys
     * are pressed down.
     */
    onClick: PropTypes.func,

    /**
     * Callback fired on <code>SPACE</code> or <code>RETURN</code> key down.
     */
    onKeyDown: PropTypes.func,

    /**
     * Content of the tooltip. This property is required if a tooltip is desired.
     */
    tooltip: PropTypes.node,
};

const Link = ({
    children,
    className,
    disabled,
    href,
    onClick,
    onKeyDown,
    tooltip,
    nodeRef,
    ...rest
}) => {
    const focusCSS = ({ linkBlue }) => css`
        &:focus-visible {
            outline: solid;
            outline-color: ${linkBlue};
            outline-offset: 1px;
            border-radius: 2px;
            outline-width: 2px;
        }

        &:hover,
        &:focus-visible {
            text-decoration: underline;
            text-decoration-color: ${linkBlue};
        }
    `;

    return (
        <CorgixLink
            className={className}
            disabled={disabled}
            href={href}
            onClick={onClick}
            onKeyDown={onKeyDown}
            tooltip={tooltip}
            nodeRef={nodeRef}
            css={focusCSS}
            tabIndex={0}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...rest}
        >
            {children}
        </CorgixLink>
    );
};

Link.propTypes = propTypes;
export default Link;
